// src/pages/orders/ModifyOrder.js

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import API_BASE_URL from '../../utils/config';

const ModifyOrder = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const { orderId, invoiceId } = useParams();
  const isAfterInvoice = Boolean(invoiceId);

  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [products, setProducts] = useState([]);
  const [orderItems, setOrderItems] = useState([]);
  const [formData, setFormData] = useState({
    customer_id: '',
    doctor_id: '',
  });

  // Load dropdown data & order details
  useEffect(() => {
    Promise.all([
      axios.get(`${API_BASE_URL}/api/customers`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API_BASE_URL}/api/doctors`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API_BASE_URL}/api/products/batches`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API_BASE_URL}/api/orders/${orderId}`, { headers: { Authorization: `Bearer ${token}` } })
    ])
      .then(([custRes, docRes, prodRes, orderRes]) => {
        setCustomers(custRes.data);
        setDoctors(docRes.data);
        setProducts(prodRes.data);

        setFormData({
          customer_id: orderRes.data.customer_id,
          doctor_id: orderRes.data.doctor_id,
        });

        const formattedItems = orderRes.data.items.map(it => ({
          product_id: it.product_id,
          mrp: it.mrp,
          quantity: it.quantity,
          unit_price: parseFloat(it.unit_price).toFixed(2),
          margin: it.margin,
          total_price: it.total_price,
          gst: it.gst,
          expiry_date: it.expiry_date ? new Date(it.expiry_date).toISOString().split('T')[0] : '',
          discount: it.discount,
          scheme_quantity: it.scheme_quantity
        }));
        setOrderItems(formattedItems);
      })
      .catch(err => {
        console.error('Error loading data', err);
        alert('Failed to load order');
      })
      .finally(() => setLoading(false));
  }, [orderId, token]);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const addItem = () => {
    setOrderItems(prev => [
      ...prev,
      {
        product_id: '',
        mrp: 0,
        quantity: 1,
        unit_price: 0,
        margin: 0,
        total_price: 0,
        gst: 0,
        expiry_date: '',
        discount: 0,
        scheme_quantity: 0,
      }
    ]);
  };

  const removeItem = (index) => {
    setOrderItems(prev => prev.filter((_, i) => i !== index));
  };

  const updateItem = async (index, key, value) => {
    const newItems = [...orderItems];
    let item = { ...newItems[index] };

    if (key === 'product_id') {
      const prod = products.find(p => p.id === Number(value));
      if (prod) {
        item.product_id = prod.id;
        item.mrp = prod.mrp;
        item.margin = 20;
        item.gst = parseFloat(prod.tax_percent) || 0;
        item.unit_price = ((item.mrp / (1 + (item.gst / 100))) * (1 - (item.margin / 100))).toFixed(2) || 0;

        try {
          const res = await axios.get(`${API_BASE_URL}/api/batches/${prod.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res.data?.expiry_date) {
            item.expiry_date = new Date(res.data.expiry_date).toISOString().split('T')[0];
          }
        } catch (err) {
          console.error('Error fetching expiry:', err);
        }
      }
    }
    else if (key === 'margin') {
      item.margin = parseFloat(value) || 20;
    }
    else if (key === 'quantity') {
      item.quantity = parseInt(value) || 1;
    }
    else if (key === 'unit_price') {
      item.unit_price = ((item.mrp / (1 + (item.gst / 100))) * (1 - (item.margin / 100))).toFixed(2) || 0;
    }
    else if (key === 'discount') {
      item.discount = parseFloat(value) || 0;
    }
    else if (key === 'scheme_quantity') {
      item.scheme_quantity = parseInt(value) || 0;
    }

    const baseRate = parseFloat(item.unit_price);
    const discountAmt = baseRate * (item.discount || 0) / 100;
    const gstAmt = (baseRate - discountAmt) * (item.gst || 0) / 100;
    item.total_price = parseFloat(((baseRate - discountAmt + gstAmt) * item.quantity).toFixed(2));

    newItems[index] = item;
    setOrderItems(newItems);
  };

  const calculateTotals = () => {
    let subtotal = 0, totalGST = 0, total = 0, totalDiscount = 0;
    orderItems.forEach(item => {
      const baseRate = parseFloat(item.unit_price);
      const discountAmt = baseRate * (item.discount || 0) / 100;
      const gstAmt = (baseRate - discountAmt) * (item.gst || 0) / 100;

      subtotal += (baseRate - discountAmt) * item.quantity;
      totalGST += gstAmt * item.quantity;
      totalDiscount += discountAmt * item.quantity;
      total += (baseRate - discountAmt + gstAmt) * item.quantity;
    });
    return { subtotal, totalGST, total, totalDiscount };
  };

  const { subtotal, totalGST, total, totalDiscount } = calculateTotals();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id || !formData.doctor_id) return alert('Select customer and doctor');
    if (orderItems.length === 0) return alert('Add at least one product');

    try {
      await axios.put(`${API_BASE_URL}/api/orders/${orderId}`, {
        ...formData,
        items: orderItems
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('Order updated successfully');
      if (isAfterInvoice) {
        navigate(`/invoices/${invoiceId}`);
      } else {
        navigate(`/orders`);
      }
    } catch (err) {
      console.error('Modify order error:', err);
      alert('Failed to update order/invoice');
    }
  };

  if (loading) {
    return <div className="p-4">Loading order...</div>;
  }

  return (
    <div className="p-4 max-w-6xl mx-auto bg-white rounded shadow space-y-4">
      <h2 className="text-2xl font-bold mb-4">Modify Order</h2>

      <select
        name="customer_id"
        value={formData.customer_id}
        onChange={handleChange}
        className="w-full border px-3 py-2 rounded"
      >
        <option value="">Select Customer</option>
        {customers.map(c => (
          <option key={c.id} value={c.id}>{c.name}</option>
        ))}
      </select>

      <select
        name="doctor_id"
        value={formData.doctor_id}
        onChange={handleChange}
        className="w-full border px-3 py-2 rounded"
      >
        <option value="">Select Doctor</option>
        {doctors.map(d => (
          <option key={d.id} value={d.id}>{d.name}</option>
        ))}
      </select>

      <div className="space-y-2">
        {orderItems.length > 0 && (
          <div className="grid grid-cols-12 gap-2 font-semibold bg-gray-100 p-2 rounded">
            <div className="col-span-2">Product</div>
            <div className="col-span-1">MRP</div>
            <div className="col-span-1">Qty</div>
            <div className="col-span-1">Rate</div>
            <div className="col-span-1">Margin %</div>
            <div className="col-span-1">Disc %</div>
            <div className="col-span-1">Sch</div>
            <div className="col-span-1">GST %</div>
            <div className="col-span-1">Expiry</div>
            <div className="col-span-1">Total Price</div>
          </div>
        )}
        {orderItems.map((item, idx) => (
          <div key={idx} className="grid grid-cols-12 gap-2 items-center">
            <select
              value={item.product_id}
              onChange={(e) => updateItem(idx, 'product_id', e.target.value)}
              className="col-span-2 border px-2 py-1 rounded"
            >
              <option value="">Select Product</option>
              {products.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>

            <input type="number" value={item.mrp} readOnly className="col-span-1 border px-2 py-1 rounded bg-gray-100" />

            <input type="number" min={1} value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', e.target.value)} className="col-span-1 border px-2 py-1 rounded" />

            <input type="number" value={item.unit_price} onChange={(e) => updateItem(idx, 'unit_price', e.target.value)} className="col-span-1 border px-2 py-1 rounded" />

            <input type="number" value={item.margin} onChange={(e) => updateItem(idx, 'margin', e.target.value)} className="col-span-1 border px-2 py-1 rounded" />

            <input type="number" value={item.discount} onChange={(e) => updateItem(idx, 'discount', e.target.value)} className="col-span-1 border px-2 py-1 rounded" />

            <input type="number" value={item.scheme_quantity} onChange={(e) => updateItem(idx, 'scheme_quantity', e.target.value)} className="col-span-1 border px-2 py-1 rounded" />

            <input type="number" value={item.gst} readOnly className="col-span-1 border px-2 py-1 rounded bg-gray-100" />

            <input type="date" value={item.expiry_date} readOnly className="col-span-1 border px-2 py-1 rounded bg-gray-100" />

            <input type="number" value={item.total_price} readOnly className="col-span-1 border px-2 py-1 rounded bg-gray-100" />

            <button onClick={() => removeItem(idx)} className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600">Delete</button>
          </div>
        ))}
      </div>

      <button onClick={addItem} className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600">
        Add Product
      </button>

      <div className="grid grid-cols-3 gap-4 mt-4 text-gray-700 font-semibold">
        <p>Subtotal: ₹{subtotal.toFixed(2)}</p>
        <p>Discount: ₹{totalDiscount.toFixed(2)}</p>
        <p>Total GST: ₹{totalGST.toFixed(2)}</p>
        <p>Total: ₹{total.toFixed(2)}</p>
      </div>

      <button onClick={handleSubmit} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
        Save Changes
      </button>
    </div>
  );
  
}

export default ModifyOrder ;
